/** Keep in sync with server/constants_and_utils/simulation/chatTimings.js */

function intentEmbeddingTiming(timings) {
  if (!timings || typeof timings !== 'object') return null;
  const emb = timings.intentEmbeddingTiming;
  return emb && typeof emb === 'object' ? emb : null;
}

function timingsUseIntent30(timings) {
  const emb = intentEmbeddingTiming(timings);
  if (emb?.intentPipelineVersion === 3) return true;
  if (timings?.intentPipelineVersion === 3) return true;
  if (timings?.jevTargeting != null && typeof timings.jevTargeting === 'object') return true;
  if (emb?.postIntentDisambiguationSource === 'jev') return true;
  return false;
}

function patientPromptDurationMs(timings) {
  const emb = intentEmbeddingTiming(timings);
  const fromEmb = emb?.patientPrompt?.duration_ms;
  if (typeof fromEmb === 'number' && !Number.isNaN(fromEmb) && fromEmb > 0) return fromEmb;
  const top = timings?.patientLlmMs;
  if (typeof top === 'number' && !Number.isNaN(top) && top > 0) return top;
  return null;
}

function patientPromptUsedForAssistantResponse(timings) {
  const emb = intentEmbeddingTiming(timings);
  if (emb?.patientPromptUsedForResponse === true) return true;
  if (timings?.intent20PatientMessageFromPipeline === true) return true;
  return false;
}

function jevTargetingDurationMs(timings) {
  const ms = timings?.jevTargeting?.duration_ms;
  return typeof ms === 'number' && !Number.isNaN(ms) && ms > 0 ? ms : null;
}

export function targetChipMs(timings) {
  if (!timings || typeof timings !== 'object') return 0;
  const target = Number(timings.targetIdentification);
  if (Number.isFinite(target) && target > 0) return target;
  return jevTargetingDurationMs(timings) || 0;
}

export function requestWallClockMs(timings) {
  const ms = Number(timings?.requestDurationMs);
  return Number.isFinite(ms) && ms > 0 ? Math.round(ms) : null;
}

export function sumIntentEmbeddingPhaseMs(detail, { excludePatientPrompt = false, excludePipelinePrep = false } = {}) {
  if (!detail || typeof detail !== 'object') return null;
  let sum = 0;
  let any = false;
  const add = (v) => {
    if (typeof v === 'number' && !Number.isNaN(v) && v > 0) {
      sum += v;
      any = true;
    }
  };
  add(detail.segmentationMs);
  add(detail.embeddingMs);
  add(detail.retrievalMs);
  add(detail.lightweightRoutingDurationMs);
  add(detail.medical_history_extraction?.duration_ms);
  add(detail.llmPostIntentDisambiguationMs);
  add(detail.interrogativeIntentAuditMs ?? detail.interrogativeIntentAudit?.duration_ms);
  add(detail.enrichmentAfterMatchMs);
  if (!excludePipelinePrep) add(detail.pipelinePrepRemainderMs);
  if (!excludePatientPrompt) add(detail.patientPrompt?.duration_ms);
  return any ? Math.round(sum) : null;
}

export function sumAssistantTimingChipsMs(timings) {
  if (!timings || typeof timings !== 'object') return 0;
  return (
    (Number(timings.translation) || 0) +
    targetChipMs(timings) +
    (Number(timings.intentIdentification) || 0) +
    (Number(timings.messageGeneration) || 0)
  );
}

export function intent30PatientMessageGenerationIndependent(timings) {
  if (!timingsUseIntent30(timings)) return false;
  if (!patientPromptUsedForAssistantResponse(timings)) return false;
  return patientPromptDurationMs(timings) != null;
}

export function intent20PatientMessageCombinedWithIntent(timings) {
  if (timingsUseIntent30(timings)) return false;
  if (!patientPromptUsedForAssistantResponse(timings)) return false;
  return patientPromptDurationMs(timings) != null;
}

export function intentEmbeddingDetailSeparatesPatientMessageToGenerationChip(detail) {
  if (!detail || typeof detail !== 'object') return false;
  return intent30PatientMessageGenerationIndependent({
    intentPipelineVersion: detail.intentPipelineVersion,
    intentEmbeddingTiming: detail,
  });
}

export function applyAssistantTimingChipAdjustments(timings) {
  if (!timings || typeof timings !== 'object') return timings;
  const out = { ...timings };

  if (intent30PatientMessageGenerationIndependent(out)) {
    const patientMs = patientPromptDurationMs(out);
    if (patientMs != null && !(Number(out.messageGeneration) > 0)) {
      out.messageGeneration = patientMs;
    }
  }

  return out;
}

export function reconcileTimingsToRequestDuration(timings) {
  if (!timings || typeof timings !== 'object') return timings;
  const requestMs = requestWallClockMs(timings);
  if (requestMs == null) return timings;

  const out = { ...timings };
  const translation = Number(out.translation) || 0;
  const target = targetChipMs(out);
  const generation = Number(out.messageGeneration) || 0;
  out.intentIdentification = Math.max(0, Math.round(requestMs - translation - target - generation));

  const emb = intentEmbeddingTiming(out);
  if (emb) {
    const excludePatient = intent30PatientMessageGenerationIndependent(out);
    const phaseSum = sumIntentEmbeddingPhaseMs(emb, { excludePatientPrompt: excludePatient }) || 0;
    const routeRem = Math.round(out.intentIdentification - phaseSum);
    const embNext = { ...emb };
    if (routeRem > 0) {
      embNext.routeProcessingRemainderMs = routeRem;
    } else {
      delete embNext.routeProcessingRemainderMs;
    }
    out.intentEmbeddingTiming = embNext;
  }

  return out;
}

export function applyAssistantTimingDisplay(timings) {
  return reconcileTimingsToRequestDuration(applyAssistantTimingChipAdjustments(timings));
}

export function assistantChipTimings(rawTimings) {
  if (!rawTimings || typeof rawTimings !== 'object') return rawTimings;
  return applyAssistantTimingDisplay({ ...rawTimings });
}

export function assistantChipLabelForKey(key, timings) {
  if (key === 'intentIdentification' && intent20PatientMessageCombinedWithIntent(timings)) {
    return 'Intent + Message';
  }
  if (key === 'messageGeneration') return 'Generation';
  if (key === 'intentIdentification') return 'Intent';
  if (key === 'translation') return 'Translation';
  if (key === 'targetIdentification') return 'Target';
  if (key === 'patientLlmMs') return 'Patient LLM';
  return key;
}
